import { useState } from 'react';
import { sx } from '../../ui/designStyles';
import { MY_DAY_NOT_CAUGHT, useMyDay } from './useMyDay';
import { Act, D, S } from './ui';

// My day on Home's Today (design V4 T11673, mdShow). The rows, the progress, the family
// suggestions, "Add" and "Tell Atlas" are all real (useMyDay). When the server
// will not give this person's day, the block says so in its own words, never a blank.

const BTN_HELP = 'display:inline-flex;align-items:center;justify-content:center;min-height:52px;padding:0 16px;border-radius:12px;font-size:16px;font-weight:700;color:#264C7E;border:1.5px solid rgba(47,92,151,.45);text-decoration:none;white-space:nowrap;';
const BTN_DO = 'display:inline-flex;align-items:center;justify-content:center;min-height:52px;padding:0 20px;border-radius:12px;font-size:17px;font-weight:700;color:#fff;background:#1F6B52;text-decoration:none;white-space:nowrap;';
const BTN_GLASS = 'display:inline-flex;align-items:center;justify-content:center;min-height:52px;padding:0 18px;border-radius:12px;font-size:17px;font-weight:700;color:#fff;background:#2F5C97;text-decoration:none;white-space:nowrap;';

export function MyDayBlock({ personId, readOnly, reason }: { personId: string | null; readOnly: boolean; reason: string | null }) {
  const md = useMyDay({ personId, variant: 'member', readOnly });
  const [ask, setAsk] = useState('');
  const [reply, setReply] = useState('');
  const tell = async () => {
    const v = ask.trim();
    if (!v || readOnly) return;
    setAsk('');
    setReply((await md.intent(v)) ?? MY_DAY_NOT_CAUGHT);
  };
  const unavailable = md.error
    ? md.error.kind === 'forbidden'
      ? 'My day is the member’s own list. Aithora staff cannot open it, so it is not shown while viewing as the member.'
      : md.error.kind === 'not_found'
        ? 'My day is not switched on for this account yet. Nothing is shown rather than a made-up list.'
        : `My day did not load: ${md.error.message}`
    : null;
  return (
    <D css="grid-column:1/-1;box-sizing:border-box;min-width:0;padding-top:14px;border-top:1px solid rgba(34,39,46,.16);animation:riseIn .46s cubic-bezier(.22,.61,.36,1);animation-delay:90ms;" testId="my-day">
      <D css="display:flex;align-items:baseline;gap:6px 14px;flex-wrap:wrap;min-width:0;">
        <S css="font-size:16px;font-weight:700;letter-spacing:.12em;text-transform:uppercase;color:#22272E;">My day</S>
        <S css="font-size:17px;font-weight:700;color:#1F6B52;font-variant-numeric:tabular-nums;">{md.progress}</S>
        <S css="font-size:16px;color:#54606C;">{md.streak ?? ''}</S>
        <S css="flex:1 1 10px;" />
        <S css="font-size:16px;color:#54606C;">Or tell Atlas {'“'}I did it{'”'}</S>
      </D>
      <D css="height:8px;border-radius:4px;background:rgba(34,39,46,.08);margin:10px 0 4px;overflow:hidden;">
        <div style={{ ...sx('height:100%;border-radius:4px;background:#1F6B52;transition:width .5s cubic-bezier(.22,.61,.36,1);'), width: md.pct }} />
      </D>
      {md.shown.map((d) => (
        <D key={d.id} css={d.rowStyle} testId="my-day-item">
          <S css="flex:0 0 64px;font-size:16px;font-weight:700;color:#54606C;font-variant-numeric:tabular-nums;;white-space:nowrap;">{d.time}</S>
          <S css="flex:1 1 220px;min-width:0;display:flex;flex-direction:column;gap:3px;">
            <S css="display:flex;align-items:baseline;gap:10px;flex-wrap:wrap;">
              <S css="font-size:19px;font-weight:700;color:#22272E;line-height:1.3;">{d.label}</S>
              <S css={d.tagStyle}>{d.tag}</S>
            </S>
            <S css="font-size:16px;color:#54606C;line-height:1.4;">{d.why}</S>
          </S>
          <S css="flex:0 0 auto;display:flex;align-items:center;gap:8px;flex-wrap:wrap;justify-content:flex-end;">
            {d.isWater && (
              <>
                <S css="display:inline-flex;align-items:center;gap:4px;" label={d.waterText}>
                  {d.glasses.map((g, i) => (
                    <S key={i} css={g} />
                  ))}
                </S>
                <S css="font-size:16px;font-weight:700;color:#264C7E;font-variant-numeric:tabular-nums;white-space:nowrap;">{d.waterText}</S>
                <Act css={BTN_GLASS} onClick={d.onGlass} disabled={readOnly} reason={reason}>
                  + A glass
                </Act>
              </>
            )}
            {d.canDo && (
              <>
                <Act css={BTN_HELP} onClick={d.onHelp} disabled={readOnly} reason={reason}>
                  With help
                </Act>
                <Act css={BTN_DO} onClick={d.onDo} disabled={readOnly} reason={reason}>
                  I did it
                </Act>
              </>
            )}
            {d.isDone && (
              <>
                <S css="font-size:17px;font-weight:700;color:#1F6B52;white-space:nowrap;">{'✓ '}{d.doneText}</S>
                <Act css="display:inline-flex;align-items:center;min-height:44px;padding:0 6px;font-size:16px;font-weight:700;color:#2F5C97;text-decoration:none;" onClick={d.onUndo} disabled={readOnly} reason={reason}>
                  Undo
                </Act>
              </>
            )}
          </S>
        </D>
      ))}
      {md.moreLabel && (
        // Opening the rest of the day writes nothing, so it works in a read-only view too.
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            md.toggleMore();
          }}
          style={sx('display:flex;align-items:center;gap:10px;min-height:60px;padding:0 4px;border-top:1px solid rgba(34,39,46,.08);font-size:19px;font-weight:700;color:#2F5C97;text-decoration:none;')}
        >
          {md.moreLabel}
        </a>
      )}
      {md.suggestions.map((x) => (
        <D key={x.id} css="display:flex;align-items:center;gap:10px 16px;flex-wrap:wrap;min-width:0;padding:12px 14px;margin-top:10px;border-radius:12px;background:rgba(47,92,151,.07);">
          <S css="flex:1 1 240px;min-width:0;font-size:17px;color:#22272E;line-height:1.4;">
            <b>{x.from}</b> suggests: {x.what}
          </S>
          <Act css="display:inline-flex;align-items:center;min-height:52px;padding:0 16px;border-radius:12px;font-size:16px;font-weight:700;color:#3C454F;border:1.5px solid rgba(34,39,46,.25);text-decoration:none;" onClick={x.onNo} disabled={readOnly} reason={reason}>
            Not now
          </Act>
          <Act css="display:inline-flex;align-items:center;min-height:52px;padding:0 18px;border-radius:12px;font-size:17px;font-weight:700;color:#fff;background:#2F5C97;text-decoration:none;" onClick={x.onYes} disabled={readOnly} reason={reason}>
            Add to my day
          </Act>
        </D>
      ))}
      <D css="display:flex;align-items:center;gap:10px;flex-wrap:wrap;min-width:0;margin-top:12px;">
        <input
          value={md.draft}
          onChange={(e) => md.setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              md.add();
            }
          }}
          disabled={readOnly}
          placeholder="Add something for today, like Water the plant"
          aria-label="Add something for today"
          style={sx('flex:1 1 260px;min-width:0;min-height:52px;box-sizing:border-box;padding:0 16px;border-radius:12px;border:1.5px solid rgba(34,39,46,.2);background:#FCFAF4;font-family:Archivo,system-ui,sans-serif;font-size:17px;color:#22272E;')}
        />
        <Act css="display:inline-flex;align-items:center;min-height:52px;padding:0 20px;border-radius:12px;font-size:17px;font-weight:700;color:#FCFAF4;background:#22272E;text-decoration:none;" onClick={md.add} disabled={readOnly} reason={reason}>
          Add
        </Act>
      </D>
      <D css="display:flex;align-items:center;gap:10px;flex-wrap:wrap;min-width:0;margin-top:10px;padding:10px 12px;border-radius:12px;background:rgba(47,92,151,.07);">
        <S css="flex:0 0 auto;font-size:16px;font-weight:700;color:#264C7E;">Tell Atlas</S>
        <input
          value={ask}
          onChange={(e) => setAsk(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void tell();
            }
          }}
          disabled={readOnly}
          placeholder={'I took my pills · I had two glasses of water'}
          aria-label="Tell Atlas"
          style={sx('flex:1 1 240px;min-width:0;min-height:52px;box-sizing:border-box;padding:0 16px;border-radius:12px;border:1.5px solid rgba(47,92,151,.3);background:#FCFAF4;font-family:Archivo,system-ui,sans-serif;font-size:17px;color:#22272E;')}
        />
        <Act css="display:inline-flex;align-items:center;min-height:52px;padding:0 20px;border-radius:12px;font-size:17px;font-weight:700;color:#fff;background:#2F5C97;text-decoration:none;" onClick={() => void tell()} disabled={readOnly} reason={reason}>
          Tell
        </Act>
        {reply && <S css="flex:1 1 100%;font-size:17px;color:#22272E;line-height:1.45;">{reply}</S>}
      </D>
      <D css="font-size:16px;color:#54606C;line-height:1.5;margin-top:10px;text-wrap:pretty;">{unavailable ?? md.privacy}</D>
    </D>
  );
}
